/**
 * Контекст «Анализа»: закреплённые метрики, дисциплина, период.
 *
 * ЗАЧЕМ. Путь профи до своих метрик должен быть в ноль касаний
 * (MOBILE_NAV_ARCHITECTURE.md раздел 4) — ровно этим intervals.icu держит
 * сильных атлетов. Здесь лежит вся логика хранения этого контекста,
 * отвязанная от React: чистые функции проверяются без рендера.
 *
 * ПРАВИЛО 4 СВЯЗОК — «сквозной, но не липкий»: дисциплина и период живут
 * между «Формой» и «Анализом» и переживают уход с экрана, но сбрасываются
 * при смене цели. Иначе атлет месяцами смотрит данные через фильтр,
 * о котором забыл. Поэтому дисциплина хранится ВМЕСТЕ с id цели, при
 * которой её выбрали, и при чтении сверяется с текущей.
 */

import type { Sport, SportFilter } from "./analyticsApi";

export const ANALYSIS_PINS_KEY = "storica.analysis.pins.v1";
export const ANALYSIS_DISC_KEY = "storica.analysis.disc.v1";
export const ANALYSIS_PERIOD_KEY = "storica.analysis.period.v1";
/**
 * Произвольное окно «от и до». Отдельным ключом от пресета: пресет —
 * перечисление, и пара дат внутри него сломала бы чтение старых значений.
 */
export const ANALYSIS_CUSTOM_RANGE_KEY = "storica.analysis.custom_range.v1";

/** Больше пяти закреплённых — это уже не «своё», а вторая лента. */
export const MAX_PINS = 5;

/**
 * Дисциплина как её видит атлет, не как хранит бэкенд.
 *
 * ПОЧЕМУ БОЛЬШЕ ТРЁХ. Здесь было `swim | ride | run` — и это оставляло без
 * дисциплинарного контекста всех, кто занимается не этими тремя: силовику и
 * hyrox-атлету переключатель не показывался вовсе, потому что показывать было
 * нечего. При этом Аналитика фильтр по их видам ЗНАЛА (чипы `SportFilter`), и
 * выбор, сделанный там, при возврате на «Форму» терялся молча.
 *
 * Под «бегом» по-прежнему трейл: это под-тип базы, а не отдельный вид
 * (таксономия B8, `shared/domain/discipline.py`). Под «вело» — гравий и MTB
 * по той же причине.
 *
 * Триатлон и дуатлон сюда НЕ попадают намеренно: это не вид нагрузки, а
 * формат старта, и тренировка внутри них всё равно записана плаванием, вело
 * или бегом. Кнопка «триатлон» отфильтровала бы ровно те тренировки, которые
 * помечены составным видом, — то есть почти ничего.
 *
 * Ключ вело — `ride`, как во всём каноне (v203; прежде здесь было `bike`).
 * Значение попадает в URL (`?disc=`) и в localStorage, поэтому ЧТЕНИЕ обязано
 * принимать и прежнее имя: у людей остались сохранённые ссылки и состояние,
 * а данные переживают код. За это отвечает `parseStoredDisc`.
 */
export type AnalysisDiscipline = "all" | "swim" | "ride" | "run" | "strength" | "hyrox";

export const DISCIPLINES: AnalysisDiscipline[] = [
  "all", "swim", "ride", "run", "strength", "hyrox",
];

/** Прежние имена ключа, которые могли осесть в ссылках и localStorage. */
const LEGACY_DISC_ALIASES: Record<string, AnalysisDiscipline> = {
  bike: "ride",
  cycle: "ride",
};

/**
 * Разбор значения из URL или хранилища с поддержкой прежних имён.
 * Неизвестное — null, чтобы вызывающий сам решил, что делать.
 */
export function parseStoredDisc(raw: unknown): AnalysisDiscipline | null {
  if (typeof raw !== "string") return null;
  const v = raw.toLowerCase();
  if ((DISCIPLINES as string[]).includes(v)) return v as AnalysisDiscipline;
  return LEGACY_DISC_ALIASES[v] ?? null;
}

const DISC_TO_SPORTS: Record<AnalysisDiscipline, Sport[]> = {
  all: [],
  swim: ["swim"],
  ride: ["cycle"],
  run: ["run", "trail"],
  strength: ["strength"],
  hyrox: ["hyrox"],
};

/** Фильтр для analytics-хуков: пустой массив = «все» (контракт analyticsApi). */
export function discToSports(disc: AnalysisDiscipline): SportFilter {
  return DISC_TO_SPORTS[disc];
}

/**
 * Бэкенд и интеграции называют дисциплины по-разному (cycle/bike/ride/
 * gravel/mtb — всё вело). Приводим к трём кнопкам атлета; неизвестное —
 * null, не «ближайшее похожее» (Walserwäg-правило).
 */
export function normalizeDiscipline(raw: string): Exclude<AnalysisDiscipline, "all"> | null {
  const v = raw.toLowerCase();
  if (["swim", "swimming"].includes(v)) return "swim";
  if (["bike", "cycle", "ride", "cycling", "gravel", "mtb"].includes(v)) return "ride";
  if (["run", "running", "trail"].includes(v)) return "run";
  if (["strength", "gym", "weights"].includes(v)) return "strength";
  if (["hyrox"].includes(v)) return "hyrox";
  return null;
}

/** Активные дисциплины атлета в каноническом порядке (плавание→вело→бег). */
export function activeDisciplines(raw: readonly string[] | undefined): Exclude<AnalysisDiscipline, "all">[] {
  const found = new Set((raw ?? []).map(normalizeDiscipline).filter((d): d is Exclude<AnalysisDiscipline, "all"> => d !== null));
  return (["swim", "ride", "run", "strength", "hyrox"] as const).filter((d) => found.has(d));
}

/**
 * Виды спорта атлета: то, что он ВЫБРАЛ, плюс то, чем ФАКТИЧЕСКИ занимается.
 *
 * ЗАЧЕМ ОБЪЕДИНЕНИЕ. Профиль — заявление человека о себе, и переписывать его по
 * тренировкам нельзя: он вправе считать себя бегуном, катаясь на работу. Но
 * экраны спрашивают не «кем он себя считает», а «что ему показывать», и здесь
 * профиля мало: у Димы в `disciplines` стоял один бег при девятнадцати
 * велотренировках за полгода (последняя — за день до замера). Вело-порог, таб
 * мощности и фильтр дисциплин выглядели чужими на его собственном экране, хотя
 * это его вело.
 *
 * Один хелпер вместо трёх копий: до 2026-08-04 «Форма», студия и хаб линз
 * каждый по-своему разворачивали `sportMetrics.disciplines`, и добавить к ним
 * факт значило бы править три места и не забыть ни одного.
 */
export function athleteDisciplines(
  metrics: { disciplines?: readonly string[]; disciplines_seen?: readonly string[] } | undefined,
): Exclude<AnalysisDiscipline, "all">[] {
  if (!metrics) return [];
  return activeDisciplines([
    ...(metrics.disciplines ?? []),
    ...(metrics.disciplines_seen ?? []),
  ]);
}

/**
 * Deep-link в Анализ с контекстом (правило 1 связок): дисциплина «всё»
 * в URL не пишется — это дефолт, а не выбор.
 */
export function studioLink(tab: "form" | "int" | "power" | "health" | "achieve" | "lenses" | "hist", disc: AnalysisDiscipline = "all"): string {
  const params = new URLSearchParams({ tab });
  if (disc !== "all") params.set("disc", disc);
  return `/analytics/studio?${params.toString()}`;
}

/**
 * Закрепление: добавляет в конец либо убирает. Порядок = порядок закрепления;
 * шестое закрепление не входит, пока не откреплено что-то из пяти —
 * молча выталкивать чужой выбор было бы сюрпризом.
 */
export function togglePin(pins: string[], id: string): string[] {
  if (pins.includes(id)) return pins.filter((p) => p !== id);
  if (pins.length >= MAX_PINS) return pins;
  return [...pins, id];
}

/**
 * Прочитанные из хранилища пины фильтруются по живому каталогу: карточку
 * могли переименовать или удалить, и мёртвый id не должен рендерить дыру.
 */
export function sanitizePins(raw: unknown, knownIds: ReadonlySet<string>): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((p): p is string => typeof p === "string" && knownIds.has(p))
    .slice(0, MAX_PINS);
}

/** Хранимая дисциплина помнит, при какой цели её выбрали. */
export interface StoredDiscipline {
  disc: AnalysisDiscipline;
  /** id активной цели на момент выбора; null — цели не было. */
  goalId: number | null;
}

/**
 * Чтение с проверкой правила 4: цель сменилась — контекст сбрасывается на
 * «всё». Мусор в хранилище тоже даёт «всё», а не крэш. Принимает уже
 * распарсенное значение (useLocalStorage хранит JSON сам).
 */
export function resolveDisc(stored: unknown, currentGoalId: number | null): AnalysisDiscipline {
  if (typeof stored !== "object" || stored === null) return "all";
  const { disc, goalId } = stored as Partial<StoredDiscipline>;
  const parsed = parseStoredDisc(disc);
  if (parsed === null) return "all";
  if ((goalId ?? null) !== currentGoalId) return "all";
  return parsed;
}

/**
 * НАЛОЖЕНИЕ ФИЗИОЛОГИЧЕСКОЙ МЕТРИКИ НА КРИВУЮ НАГРУЗКИ.
 *
 * Бэкенд считает шесть метрик (`SUPPORTED_METRICS`), таймлайн-хук умеет
 * запросить любой их набор, а график рисовал ровно три: CTL, ATL, TSB.
 * Остальные три — ACWR, lnRMSSD, CV HRV — приезжали в ответе и молча
 * выбрасывались. Атлет не мог посмотреть, как его вариабельность вела себя
 * на подъёме нагрузки, хотя оба ряда уже лежали в одном ответе.
 *
 * Наложение — ОДНА метрика за раз и на СВОЕЙ правой оси. Не из осторожности:
 * у ACWR (около 1.0), lnRMSSD (около 4) и CV HRV (проценты) нет общей
 * единицы, и сведение их на одну шкалу с TSS-нагрузкой нарисовало бы
 * неправду — три почти плоские линии у нуля. Общая у них ось времени, и
 * сравнение идёт по ней.
 */
export const ANALYSIS_OVERLAY_KEY = "storica.analysis.overlay.v1";

export type OverlayMetric = "none" | "acwr" | "lnrmssd" | "cv_hrv";

export const OVERLAY_METRICS: OverlayMetric[] = ["none", "acwr", "lnrmssd", "cv_hrv"];

/** Метрики, которые график рисует сам и запрашивать отдельно не нужно. */
export const BASE_TIMELINE_METRICS = ["ctl", "atl", "tsb"];

export function parseStoredOverlay(raw: string | null): OverlayMetric {
  return (OVERLAY_METRICS as string[]).includes(raw ?? "")
    ? (raw as OverlayMetric)
    : "none";
}

/** Список для `?metrics=`: база плюс наложенная, без повторов. */
export function timelineMetricsFor(overlay: OverlayMetric): string[] {
  return overlay === "none"
    ? [...BASE_TIMELINE_METRICS]
    : [...BASE_TIMELINE_METRICS, overlay];
}

/**
 * НАСТРАИВАЕМАЯ КРИВАЯ «ЛУЧШЕЕ СРЕДНЕЕ» НА ЭКРАНЕ ТРЕНИРОВКИ.
 *
 * Кривая по одной тренировке отвечает «сколько я держал 20 минут». Профи
 * задаёт следующий вопрос сразу: «а это много ДЛЯ МЕНЯ» — и ответ даёт только
 * вторая линия, конверт лучших усилий за период. Поэтому набор — что показано
 * и с чем сравнивается — выбирается атлетом и переживает уход с экрана: тот же
 * принцип «ноль касаний до своих метрик», что у закреплённых карточек.
 *
 * Метрика хранится ОТДЕЛЬНО от периода сравнения: это два независимых
 * решения. «Смотрю мощность» — свойство атлета, «сравниваю с сезоном» —
 * свойство вопроса, и связывать их значило бы сбрасывать одно при смене
 * другого.
 *
 * Выбранная метрика может быть недоступна на КОНКРЕТНОЙ тренировке (заезд без
 * пульсометра), поэтому хранимое значение — предпочтение, а не приказ:
 * `resolveCurveMetric` сверяет его со списком доступного и молча отступает к
 * тому, что тренировка может показать честно. Иначе экран обещал бы кривую,
 * которой нет.
 */
export const CURVE_METRIC_KEY = "storica.workout.curve.metric.v1";
export const CURVE_COMPARE_KEY = "storica.workout.curve.compare.v1";

export type CurveMetric = "power" | "pace" | "hr";
export type CurveCompare = "none" | "90d" | "season" | "all";

export const CURVE_METRICS: CurveMetric[] = ["power", "pace", "hr"];
export const CURVE_COMPARES: CurveCompare[] = ["none", "90d", "season", "all"];

export function parseStoredCurveMetric(raw: string | null): CurveMetric | null {
  return (CURVE_METRICS as string[]).includes(raw ?? "") ? (raw as CurveMetric) : null;
}

export function parseStoredCurveCompare(raw: string | null): CurveCompare {
  return (CURVE_COMPARES as string[]).includes(raw ?? "")
    ? (raw as CurveCompare)
    : "none";
}

/**
 * Какую метрику показать: предпочтение атлета, если тренировка её может, иначе
 * первая доступная (бэкенд отдаёт список с дефолтом дисциплины впереди).
 * Пустой список доступного — null: кривой нет вовсе, и выбирать нечего.
 */
export function resolveCurveMetric(
  preferred: CurveMetric | null,
  available: readonly string[],
): CurveMetric | null {
  if (available.length === 0) return null;
  if (preferred && available.includes(preferred)) return preferred;
  return (available[0] as CurveMetric) ?? null;
}
